import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { loadRecaptchaEnterpriseScript } from "services/recaptchaEnterprise";
import { useText } from "context/TextProvider";

const RECAPTCHA_WIDTH = 304;
const RECAPTCHA_HEIGHT = 78;

export interface RecaptchaFieldProps {
  sitekey: string;
  onChange: (token: string | null) => void;
  theme?: "light" | "dark";
  size?: "normal" | "compact";
  tabIndex?: number;
  // Called if the Enterprise script fails to load or render() throws — lets
  // the caller show a user-facing error and keep the form disabled instead
  // of leaving a blank box with no explanation.
  onError?: (message: string) => void;
}

export interface RecaptchaFieldRef {
  // A solved widget keeps showing as checked even after its token is spent
  // on a failed submit — callers must reset() it so the user can re-solve
  // immediately instead of waiting ~2 minutes for it to expire on its own.
  reset: () => void;
}

// Google's widget is a fixed 304px-wide iframe; on viewports narrower than
// that it overflows its container, so it's scaled down to fit via CSS
// transform (the standard technique for a responsive reCAPTCHA).
const RecaptchaField = forwardRef<RecaptchaFieldRef, RecaptchaFieldProps>(({ sitekey, onChange, theme, size, tabIndex, onError }, ref): JSX.Element => {
  const text = useText();
  const loadError = text.recaptcha.loadError;
  const [loaded, setLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const widgetIdRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.enterprise?.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    renderedRef.current = false;
    setLoaded(false);

    loadRecaptchaEnterpriseScript()
      .then(() => {
        if (cancelled) return;
        const enterprise = window.grecaptcha?.enterprise;
        if (!enterprise || !widgetRef.current) {
          onError?.(loadError);
          return;
        }
        enterprise.ready(() => {
          if (cancelled || renderedRef.current || !widgetRef.current) return;
          renderedRef.current = true;
          try {
            widgetIdRef.current = enterprise.render(widgetRef.current, {
              sitekey,
              theme,
              size,
              tabindex: tabIndex,
              callback: (token) => onChange(token),
              "expired-callback": () => onChange(null),
              "error-callback": () => {
                onChange(null);
                onError?.(loadError);
              },
            });
            setLoaded(true);
          } catch (err) {
            console.error("[recaptcha] render failed:", err);
            onError?.(loadError);
          }
        });
      })
      .catch((err) => {
        console.error("[recaptcha] failed to load enterprise script:", err);
        if (!cancelled) onError?.(loadError);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScale = () => setScale(Math.min(1, container.offsetWidth / RECAPTCHA_WIDTH));
    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ width: "100%", maxWidth: RECAPTCHA_WIDTH, mx: "auto", mb: "1rem" }}>
      <Box sx={{ position: "relative", width: RECAPTCHA_WIDTH, height: RECAPTCHA_HEIGHT * scale, overflow: "hidden" }}>
        {!loaded && (
          <Skeleton
            variant="rounded"
            width={RECAPTCHA_WIDTH * scale}
            height={RECAPTCHA_HEIGHT * scale}
            sx={(muiTheme) => ({
              position: "absolute",
              top: 0,
              left: 0,
              borderRadius: `${muiTheme.shape.borderRadius}px`,
            })}
          />
        )}
        <Box
          ref={widgetRef}
          sx={{ visibility: loaded ? "visible" : "hidden", transform: `scale(${scale})`, transformOrigin: "0 0" }}
        />
      </Box>
    </Box>
  );
});

RecaptchaField.displayName = "RecaptchaField";

export default RecaptchaField;
